import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, change, formValueSelector, reduxForm } from 'redux-form';
import { Form, FormGroup, Label } from 'reactstrap';
import { connect } from 'react-redux';
import { isRequired, validateNumber } from '../../../../../utils/validators';
import { tzDiff } from '../../../../../utils/date';
import { ACTIVITY_STATUS } from '../../../../../config/constants';
import FormItem from '../../../../common/forms/FormItem';
import { enumToSelectOptions } from '../../../../../utils/enum';
import TextInput from '../../../../common/forms/input/TextInput';
import MechanicSelect from '../../../../common/forms/select/MechanicSelect';
import { DATE_TIME_FORMAT } from '../../../../../config/locale';
import LocationSelect from '../../../../common/forms/select/LocationSelect';
import FormFooter from '../../../../common/forms/FormFooter';
import Modal from '../../../../common/modal/Modal';
import { optionPropTypes } from '../../../../common/forms/select/SelectPropTypes';
import { datePropTypes } from '../../../../common/forms/input/InputPropTypes';
import Select from '../../../../common/forms/select/Select';

const FORM_NAME = 'ServiceOrderExecutionForm';

const MAX_EXECUTION_HOURS_WITHOUT_WARNING = 72;

const ACTIVITY_STATUS_OPTIONS = enumToSelectOptions(ACTIVITY_STATUS);

const formSelector = formValueSelector(FORM_NAME);

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalizedValue =
    typeof value === 'string' ? value.replace(' ', 'T') : value;

  const date = new Date(normalizedValue);

  return Number.isNaN(date.getTime()) ? null : date;
};

const validateMyEndDate = (value, allValues) => {
  if (!value || !allValues.startDate) return undefined;

  const startDate = parseDateValue(allValues.startDate);
  const endDate = parseDateValue(value);

  if (!startDate || !endDate) {
    return 'Fecha inválida';
  }

  if (endDate.getTime() < startDate.getTime()) {
    return 'La fecha y hora final no puede ser menor a la fecha y hora de inicio';
  }

  return undefined;
};

const getStatusValue = (status) => {
  if (!status) return null;

  if (Object.prototype.hasOwnProperty.call(status, 'value')) {
    return status.value;
  }

  return status;
};

const validateStartDate = (value) => {
  if (!value) return undefined;

  const startDate = parseDateValue(value);

  if (!startDate) {
    return 'Fecha de inicio inválida';
  }

  const today = new Date();

  startDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  if (startDate > today) {
    return 'La fecha de inicio no puede ser mayor al día actual';
  }

  return undefined;
};

const getExecutionHours = (startDateValue, endDateValue) => {
  const parsedStartDate = parseDateValue(startDateValue);
  const parsedEndDate = parseDateValue(endDateValue);

  if (!parsedStartDate || !parsedEndDate) {
    return 0;
  }

  return (
    (parsedEndDate.getTime() - parsedStartDate.getTime()) / (1000 * 60 * 60)
  );
};

const formatDateTime = (value) => {
  const parsedDate = parseDateValue(value);

  if (!parsedDate) return '-';

  return parsedDate.toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatExecutionDuration = (executionHours) => {
  const totalHours = Math.floor(executionHours);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (hours === 0) {
    return `${days} días`;
  }

  return `${days} días y ${hours} horas`;
};

const validateAdvancePercentage = (value) => {
  if (!value && value !== 0) return undefined;

  const valueAsString = String(value).trim();

  const isValidNumber = /^\d+([.,]\d{1,2})?$/.test(valueAsString);

  if (!isValidNumber) {
    return 'Ingrese un número válido con máximo 2 decimales';
  }

  const numberValue = Number(valueAsString.replace(',', '.'));

  if (numberValue < 1 || numberValue > 100) {
    return 'Debe ser un número entre 1 y 100';
  }

  return undefined;
};

const RejectedCheckbox = () => (
  <FormGroup row>
    <FormItem>
      <Label>
        <Field
          name="rejected"
          id="rejected"
          component="input"
          type="checkbox"
        />{' '}
        Marcar el aviso de mantenimiento como rechazado
      </Label>
    </FormItem>
  </FormGroup>
);

RejectedCheckbox.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
  }).isRequired,
};

const ServiceOrderExecutionForm = ({
  handleSubmit,
  onSubmit,
  initialValues,
  dispatchChange,
  disabledStatus,
  maintenancePlannings,
  loadingMaintenancePlanningActivities,
  selectedStatus,
}) => {
  const statusRef = useRef(null);
  const extensionFactorRef = useRef(null);
  const mechanicsRef = useRef(null);
  const maintenancePlanningRef = useRef(null);

  const initialStatusValue = getStatusValue(initialValues.status);

  const selectedStatusValue =
    selectedStatus === undefined
      ? initialStatusValue
      : getStatusValue(selectedStatus);

  const [extensionFactorRequired, setExtensionFactorRequired] = useState(
    initialStatusValue === ACTIVITY_STATUS.EXTENDED.value,
  );

  const [maintenancePlanningRequired, setMaintenancePlanningRequired] =
    useState(initialStatusValue === ACTIVITY_STATUS.ADVANCE.value);

  const [showAdvancePercentage, setShowAdvancePercentage] = useState(
    initialStatusValue === ACTIVITY_STATUS.IN_PROGRESS.value,
  );

  const [startDate, setStartDate] = useState(initialValues.startDate);

  const [endDate, setEndDate] = useState(initialValues.endDate);

  const [showExecutionWarning, setShowExecutionWarning] = useState(false);

  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);

  const [pendingExecutionHours, setPendingExecutionHours] = useState(0);

  useEffect(() => {
    if (!disabledStatus && statusRef.current && statusRef.current.focus) {
      statusRef.current.focus();
    } else if (
      disabledStatus &&
      mechanicsRef.current &&
      mechanicsRef.current.focus
    ) {
      mechanicsRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (
      extensionFactorRequired &&
      extensionFactorRef.current &&
      extensionFactorRef.current.focus
    ) {
      extensionFactorRef.current.focus();
    }
  }, [extensionFactorRequired]);

  useEffect(() => {
    if (
      maintenancePlanningRequired &&
      maintenancePlanningRef.current &&
      maintenancePlanningRef.current.focus
    ) {
      maintenancePlanningRef.current.focus();
    }
  }, [maintenancePlanningRequired]);

  const closeExecutionWarning = () => {
    setShowExecutionWarning(false);
    setPendingSubmitValues(null);
    setPendingExecutionHours(0);
  };

  const continueExecutionSubmit = async () => {
    const values = pendingSubmitValues;

    closeExecutionWarning();

    if (values) {
      await onSubmit(values);
    }
  };

  const onHandleSubmit = async (values) => {
    const statusValue = getStatusValue(values.status);

    const submitValues = {
      ...values,
      rejected:
        statusValue === ACTIVITY_STATUS.DONE.value
          ? values.rejected === true || values.rejected === 'true'
          : false,
    };

    const executionHours = getExecutionHours(
      submitValues.startDate,
      submitValues.endDate,
    );

    const mustShowWarning =
      executionHours > MAX_EXECUTION_HOURS_WITHOUT_WARNING;

    if (mustShowWarning) {
      setPendingSubmitValues(submitValues);
      setPendingExecutionHours(executionHours);
      setShowExecutionWarning(true);

      return null;
    }

    return onSubmit(submitValues);
  };

  const onCalculateWorkingHours = ({ startDateSelected, endDateSelected }) => {
    let hours = tzDiff({
      startDate: startDateSelected,
      endDate: endDateSelected,
      unit: 'hours',
    });

    if (hours > 8) hours = 8;
    if (hours < 0) hours = 0;

    dispatchChange(FORM_NAME, 'workingHour', hours);
  };

  const onChangeStatus = (option) => {
    const statusValue = getStatusValue(option);

    dispatchChange(FORM_NAME, 'extensionFactor', null);

    const isExtended = statusValue === ACTIVITY_STATUS.EXTENDED.value;

    const isAdvanced = statusValue === ACTIVITY_STATUS.ADVANCE.value;

    const isInProgress = statusValue === ACTIVITY_STATUS.IN_PROGRESS.value;

    const isDone = statusValue === ACTIVITY_STATUS.DONE.value;

    setExtensionFactorRequired(isExtended);
    setMaintenancePlanningRequired(isAdvanced);
    setShowAdvancePercentage(isInProgress);

    if (!isAdvanced) {
      dispatchChange(FORM_NAME, 'maintenancePlanningId', null);
    }

    if (!isInProgress) {
      dispatchChange(FORM_NAME, 'advancePercentage', null);
    }

    if (!isDone) {
      dispatchChange(FORM_NAME, 'rejected', false);
    }

    if (isDone && mechanicsRef.current && mechanicsRef.current.focus) {
      mechanicsRef.current.focus();
    }
  };

  const onChangeStartDate = (event, value) => {
    setStartDate(value);

    onCalculateWorkingHours({
      startDateSelected: value,
      endDateSelected: endDate,
    });
  };

  const onChangeEndDate = (event, value) => {
    setEndDate(value);

    onCalculateWorkingHours({
      startDateSelected: startDate,
      endDateSelected: value,
    });
  };

  const validateExtensionFactor = [validateNumber];

  if (extensionFactorRequired) {
    validateExtensionFactor.push(isRequired);
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onHandleSubmit)}>
        <FormGroup row>
          <FormItem label="Estado" required>
            <Field
              name="status"
              component={Select}
              options={ACTIVITY_STATUS_OPTIONS.filter((option) =>
                [
                  'PENDING',
                  'DONE',
                  'EXTENDED',
                  'ADVANCE',
                  'IN_PROGRESS',
                ].includes(option.value),
              )}
              onChange={onChangeStatus}
              validate={[isRequired]}
              isClearable={false}
              setFieldToBeFocused={(input) => {
                statusRef.current = input;
              }}
              isDisabled={disabledStatus}
            />
          </FormItem>
        </FormGroup>

        {extensionFactorRequired && (
          <FormGroup row>
            <FormItem label="KM a Agregar" required>
              <Field
                name="extensionFactor"
                component={TextInput}
                type="text"
                placeholder="KM a Agregar"
                validate={validateExtensionFactor}
                setFieldToBeFocused={(input) => {
                  extensionFactorRef.current = input;
                }}
                disabled={disabledStatus}
              />
            </FormItem>
          </FormGroup>
        )}

        {maintenancePlanningRequired && (
          <FormGroup row>
            <FormItem label="Plan de Mantenimiento" required>
              <Field
                name="maintenancePlanningId"
                component={Select}
                options={maintenancePlannings}
                validate={[isRequired]}
                isClearable={false}
                isDisabled={
                  disabledStatus || loadingMaintenancePlanningActivities
                }
                placeholder="Seleccione un plan de mantenimiento"
                setFieldToBeFocused={(input) => {
                  maintenancePlanningRef.current = input;
                }}
              />
            </FormItem>
          </FormGroup>
        )}

        {showAdvancePercentage && (
          <FormGroup row>
            <FormItem label="Porcentaje de avance" required>
              <Field
                name="advancePercentage"
                component={TextInput}
                type="text"
                placeholder="Porcentaje de avance"
                validate={[isRequired, validateAdvancePercentage]}
              />
            </FormItem>
          </FormGroup>
        )}

        <FormGroup row>
          <FormItem label="Personal" required>
            <Field
              name="mechanics"
              component={MechanicSelect}
              isMulti
              validate={[isRequired]}
              setFieldToBeFocused={(input) => {
                mechanicsRef.current = input;
              }}
            />
          </FormItem>
        </FormGroup>
        {selectedStatusValue === ACTIVITY_STATUS.DONE.value && (
          <RejectedCheckbox />
        )}

        <FormGroup row>
          <FormItem label="Fecha/Hora Inicio">
            <Field
              name="startDate"
              component={TextInput}
              type="datetime-local"
              placeholder={DATE_TIME_FORMAT}
              onChange={onChangeStartDate}
              validate={[validateStartDate]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Fecha/Hora Final" required>
            <Field
              name="endDate"
              component={TextInput}
              type="datetime-local"
              placeholder={DATE_TIME_FORMAT}
              onChange={onChangeEndDate}
              validate={[isRequired, validateMyEndDate]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Horas Trabajadas">
            <Field
              name="workingHour"
              component={TextInput}
              type="text"
              placeholder="Horas Trabajadas"
              validate={[validateNumber]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Base" required>
            <Field
              name="baseLocationId"
              component={LocationSelect}
              forHomeBase
              validate={[isRequired]}
            />
          </FormItem>
        </FormGroup>

        <FormGroup row>
          <FormItem label="Observaciones">
            <Field
              name="comment"
              component={TextInput}
              type="textarea"
              placeholder="Observaciones"
            />
          </FormItem>
        </FormGroup>

        <FormFooter />
      </Form>

      <Modal
        show={showExecutionWarning}
        title="Advertencia"
        size="md"
        body={
          <div>
            <p>Está cerrando una actividad con más de 3 días de ejecución.</p>

            <p>
              <strong>Fecha/Hora Inicio:</strong>{' '}
              {formatDateTime(
                pendingSubmitValues && pendingSubmitValues.startDate,
              )}
            </p>

            <p>
              <strong>Fecha/Hora Final:</strong>{' '}
              {formatDateTime(
                pendingSubmitValues && pendingSubmitValues.endDate,
              )}
            </p>

            <p>
              <strong>Tiempo de ejecución:</strong>{' '}
              {formatExecutionDuration(pendingExecutionHours)}
            </p>

            <p>¿Desea cerrar la actividad y continuar?</p>
          </div>
        }
        onClickClose={closeExecutionWarning}
        onClickCancel={closeExecutionWarning}
        onClickConfirm={continueExecutionSubmit}
        cancelButtonText="Cerrar"
        confirmButtonText="Continuar"
      />
    </>
  );
};

ServiceOrderExecutionForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    status: PropTypes.oneOfType([optionPropTypes, PropTypes.string]),
    startDate: datePropTypes,
    endDate: datePropTypes,
    rejected: PropTypes.bool,
  }).isRequired,
  dispatchChange: PropTypes.func.isRequired,
  disabledStatus: PropTypes.bool,
  maintenancePlannings: PropTypes.arrayOf(optionPropTypes),
  loadingMaintenancePlanningActivities: PropTypes.bool,
  selectedStatus: PropTypes.oneOfType([optionPropTypes, PropTypes.string]),
};

ServiceOrderExecutionForm.defaultProps = {
  disabledStatus: false,
  maintenancePlannings: [],
  loadingMaintenancePlanningActivities: false,
  selectedStatus: undefined,
};

const mapStateToProps = (state) => {
  const maintenancePlanningContent =
    state.MechanicalMaintenanceUnit.MaintenancePlanning.getIn([
      'all',
      'content',
      'content',
    ]);

  return {
    maintenancePlannings: maintenancePlanningContent.map((planning) => ({
      value: planning.id,
      label: planning.description,
    })),

    selectedStatus: formSelector(state, 'status'),

    loadingMaintenancePlanningActivities:
      !state.MechanicalMaintenanceUnit.MaintenancePlanning.getIn([
        'current',
        'activity',
      ]).isEmpty(),
  };
};

const mapDispatchToProps = {
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: FORM_NAME,
})(ServiceOrderExecutionForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
